import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';
import AreaOptions from '../components/AreaOptions';
import Pagination, { DEFAULT_PAGE_SIZE, pagingParams } from '../components/Pagination';
import { SKILL_OPTIONS } from '../constants/registration';
import { VOLUNTEER_POST_TYPES, VOLUNTEER_TYPE_HINTS, volunteerTypeLabel } from '../constants/volunteer';
import { apiFetch, getSessionStatus } from '../utils/session';
import './HelpRequest.css';
import './Community.css';

function formatDay(iso) {
  if (!iso) return '';
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function PostCard({ post, busy, onOffer, onUpdatePost, onUpdateOffer }) {
  return (
    <article className="help-card">
      <span className="help-status" data-status={post.status === 'Open' ? 'New' : 'Closed'}>
        {post.status}
      </span>
      <h2 className="help-card-title">{post.title}</h2>
      <p className="help-meta">
        {volunteerTypeLabel(post.postType)} · {post.area} · {formatDay(post.eventDate)} · {post.acceptedCount} of{' '}
        {post.seats} places filled
        {post.openToOtherAreas ? ' · Other areas may help' : ''}
      </p>
      <p className="help-body">{post.description}</p>
      <div className="community-chips">
        {post.skillsNeeded.map((skill) => (
          <span key={skill} className="community-chip">
            {skill}
          </span>
        ))}
      </div>
      {post.myOffer ? (
        <p className="help-meta">
          Your offer: {post.myOffer.status}
          {post.myOffer.attended ? ` · Attended ${post.myOffer.attended}` : ''}
        </p>
      ) : null}
      {post.status === 'Open' &&
      post.canOffer !== false &&
      !post.createdByMe &&
      !post.myOffer &&
      post.acceptedCount < post.seats ? (
        <button type="button" className="help-btn primary" disabled={busy} onClick={() => onOffer(post._id)}>
          Offer help
        </button>
      ) : null}
      {post.createdByMe ? <p className="help-meta">You posted this need.</p> : null}
      {post.canManage ? (
        <div className="help-actions">
          <button
            type="button"
            className="help-btn"
            disabled={busy}
            onClick={() => onUpdatePost(post._id, { openToOtherAreas: !post.openToOtherAreas })}
          >
            {post.openToOtherAreas ? 'Limit to this area' : 'Allow other areas'}
          </button>
          <button
            type="button"
            className="help-btn"
            disabled={busy}
            onClick={() => onUpdatePost(post._id, { status: post.status === 'Open' ? 'Closed' : 'Open' })}
          >
            {post.status === 'Open' ? 'Close need' : 'Reopen need'}
          </button>
        </div>
      ) : null}
      {post.canManage && post.offers?.length ? (
        <ul className="help-list">
          {post.offers.map((offer) => (
            <li key={offer._id} className="help-card">
              <strong>{offer.memberName || 'Member'}</strong>
              <p className="help-meta">
                {offer.memberId || 'No member ID'} · {offer.status}
                {offer.attended ? ` · Attended ${offer.attended}` : ''}
              </p>
              <p className="help-meta">
                Skills: {offer.skills.length ? offer.skills.join(', ') : 'None listed'}
                {offer.availability ? ` · ${offer.availability}` : ''}
              </p>
              <div className="help-actions">
                {offer.status !== 'Accepted' ? (
                  <button
                    type="button"
                    className="help-btn primary"
                    disabled={busy}
                    onClick={() => onUpdateOffer(post._id, offer._id, { status: 'Accepted' })}
                  >
                    Accept
                  </button>
                ) : null}
                {offer.status !== 'Declined' ? (
                  <button
                    type="button"
                    className="help-btn"
                    disabled={busy}
                    onClick={() => onUpdateOffer(post._id, offer._id, { status: 'Declined' })}
                  >
                    Decline
                  </button>
                ) : null}
                {offer.status === 'Accepted' ? (
                  <button
                    type="button"
                    className="help-btn"
                    disabled={busy}
                    onClick={() => onUpdateOffer(post._id, offer._id, { attended: 'Yes' })}
                  >
                    Mark attended
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

export default function Volunteer() {
  const [posts, setPosts] = useState([]);
  const [area, setArea] = useState('');
  const [canCreate, setCanCreate] = useState(false);
  const [postType, setPostType] = useState('general');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [postArea, setPostArea] = useState('');
  const [seats, setSeats] = useState('2');
  const [skills, setSkills] = useState(['First Aid']);
  const [openToOtherAreas, setOpenToOtherAreas] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const role = sessionStorage.getItem('sangramUserRole');
  const approved = getSessionStatus() === 'Active';

  const load = useCallback(async () => {
    setError('');
    const res = await apiFetch(apiUrl(`/volunteer?${pagingParams(page, pageSize)}`));
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not load volunteer needs');
    setArea(data.area || '');
    setPostArea((current) => current || data.area || '');
    setCanCreate(Boolean(data.canCreate));
    setPosts(data.posts || []);
    setTotal(data.total ?? (data.posts || []).length);
  }, [page, pageSize]);

  useEffect(() => {
    if (!approved) {
      setLoading(false);
      return;
    }
    load()
      .catch((e) => setError(e.message || 'Could not load volunteer needs'))
      .finally(() => setLoading(false));
  }, [approved, load]);

  const toggleSkill = (skill) => {
    setSkills((current) =>
      current.includes(skill) ? current.filter((item) => item !== skill) : [...current, skill]
    );
  };

  const createPost = async (e) => {
    e.preventDefault();
    setBusyKey('create');
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl('/volunteer'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postType,
          title,
          description,
          eventDate,
          seats: Number(seats),
          skillsNeeded: skills,
          openToOtherAreas,
          ...(role === 'Admin' ? { area: postArea } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not create volunteer need');
        return;
      }
      setTitle('');
      setDescription('');
      setEventDate('');
      setNotice(`Need posted for ${data.post.area}.`);
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  const offer = async (id) => {
    setBusyKey(id);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(apiUrl(`/volunteer/${id}/offer`), { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not offer help');
        return;
      }
      setNotice('Offer sent. The Area Head will accept or decline it.');
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  const updatePost = async (id, body) => {
    setBusyKey(id);
    setError('');
    try {
      const res = await apiFetch(apiUrl(`/volunteer/${id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not update need');
        return;
      }
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  const updateOffer = async (postId, offerId, body) => {
    setBusyKey(offerId);
    setError('');
    try {
      const res = await apiFetch(apiUrl(`/volunteer/${postId}/offers/${offerId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not update offer');
        return;
      }
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="help-page">
      <div className="help-shell">
        <header className="help-header">
          <div>
            <h1 className="help-title">Volunteer</h1>
            <p className="help-sub">
              {area
                ? `Needs for ${area}. A post from another area appears only when that Area Head allows it.`
                : 'Volunteer needs in your area.'}
            </p>
          </div>
        </header>

        {!approved ? (
          <p className="help-error" role="alert">
            Volunteer needs are available after your membership is approved.
          </p>
        ) : null}
        {error ? (
          <p className="help-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? (
          <div className="help-notice" role="status">
            <p className="help-notice-text">{notice}</p>
            <button
              type="button"
              className="help-notice-dismiss"
              aria-label="Dismiss"
              onClick={() => setNotice('')}
            >
              ×
            </button>
          </div>
        ) : null}

        {approved && canCreate ? (
          <form className="help-form" onSubmit={createPost}>
            <h2 className="help-card-title">Ask for volunteers</h2>
            <label className="help-field">
              Type
              <select
                value={postType}
                onChange={(e) => {
                  const next = e.target.value;
                  setPostType(next);
                  if (next === 'disaster') {
                    setSkills((current) =>
                      current.length === 1 && current[0] === 'First Aid'
                        ? ['First Aid', 'Medical', 'Logistics']
                        : current
                    );
                  }
                }}
              >
                {VOLUNTEER_POST_TYPES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            {VOLUNTEER_TYPE_HINTS[postType] ? <p className="help-hint">{VOLUNTEER_TYPE_HINTS[postType]}</p> : null}
            <label className="help-field">
              Title
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required />
            </label>
            <label className="help-field">
              What is needed?
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} required />
            </label>
            {role === 'Admin' ? (
              <label className="help-field">
                Area
                <select value={postArea} onChange={(e) => setPostArea(e.target.value)} required>
                  <AreaOptions current={postArea} />
                </select>
              </label>
            ) : (
              <p className="help-hint">This need is posted for {area || 'your area'}.</p>
            )}
            <label className="help-field">
              Date
              <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} required />
            </label>
            <label className="help-field">
              How many people?
              <input
                type="number"
                min="1"
                max="500"
                value={seats}
                onChange={(e) => setSeats(e.target.value)}
                required
              />
            </label>
            <fieldset className="help-field">
              <legend>Skills needed</legend>
              {SKILL_OPTIONS.map((skill) => (
                <label key={skill} className="help-hint">
                  <input
                    type="checkbox"
                    checked={skills.includes(skill)}
                    onChange={() => toggleSkill(skill)}
                  />{' '}
                  {skill}
                </label>
              ))}
            </fieldset>
            <label className="help-hint">
              <input
                type="checkbox"
                checked={openToOtherAreas}
                onChange={(e) => setOpenToOtherAreas(e.target.checked)}
              />{' '}
              Members from other areas may offer help
            </label>
            <button type="submit" className="help-btn primary" disabled={Boolean(busyKey) || skills.length === 0}>
              {busyKey === 'create' ? 'Saving…' : 'Post need'}
            </button>
          </form>
        ) : null}

        {loading ? <p className="help-muted">Loading…</p> : null}
        {approved && !loading && posts.length === 0 ? (
          <p className="help-muted">No volunteer needs in your area yet.</p>
        ) : null}
        <ul className="help-list">
          {posts.map((post) => (
            <li key={post._id}>
              <PostCard
                post={post}
                busy={Boolean(busyKey)}
                onOffer={offer}
                onUpdatePost={updatePost}
                onUpdateOffer={updateOffer}
              />
            </li>
          ))}
        </ul>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
